import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "@/components/states";
import { supabase } from "@/integrations/supabase/client";
import { FamilyReports } from "@/components/FamilyReports";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import { TrendingUp, Clock, PieChart as PieIcon, Award } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/date-format";

export const Route = createFileRoute("/_app/stats")({
  head: () => ({
    meta: [
      { title: "Statistiche — Lumen" },
      { name: "description", content: "Ore studiate, materie, XP e accuratezza dei quiz nel tempo." },
    ],
  }),
  component: StatsPage,
});

const PALETTE = ["#93c5fd", "#a7f3d0", "#c4b5fd", "#fecaca", "#fde68a", "#fca5a5"];

function StatsPage() {
  const { t } = useTranslation();
  // Sessions per day (last 14d)
  const sessionsQ = useQuery({
    queryKey: ["stats", "sessions"],
    queryFn: async () => {
      const since = new Date(Date.now() - 14 * 86400_000).toISOString();
      const { data, error } = await supabase
        .from("study_sessions")
        .select("id,started_at,duration_minutes,subject_id")
        .gte("started_at", since);
      if (error) throw error;
      return data ?? [];
    },
  });

  const sessions = sessionsQ.data ?? [];

  const { data: subjects = [] } = useQuery({
    queryKey: ["stats", "subjects"],
    queryFn: async () => {
      const { data } = await supabase.from("subjects").select("id,name");
      return data ?? [];
    },
  });

  const { data: attempts = [] } = useQuery({
    queryKey: ["stats", "attempts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("quiz_attempts")
        .select("id,score,total,created_at")
        .order("created_at", { ascending: true })
        .limit(30);
      return data ?? [];
    },
  });

  const perDay = useMemo(() => {
    const days: { day: string; minuti: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400_000);
      const key = d.toISOString().slice(0, 10);
      const label = formatDate(d, { day: "2-digit", month: "2-digit" });
      const min = sessions
        .filter((s) => s.started_at.startsWith(key))
        .reduce((sum, s) => sum + (s.duration_minutes ?? 0), 0);
      days.push({ day: label, minuti: min });
    }
    return days;
  }, [sessions]);

  const bySubject = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of sessions) {
      const name = subjects.find((x) => x.id === s.subject_id)?.name ?? t("stats.otherSubject");
      map.set(name, (map.get(name) ?? 0) + (s.duration_minutes ?? 0));
    }
    return Array.from(map, ([name, minuti]) => ({ name, minuti })).filter((x) => x.minuti > 0);
  }, [sessions, subjects, t]);

  const accuracy = useMemo(
    () =>
      attempts.map((a, i) => ({
        n: i + 1,
        acc: a.total ? Math.round((a.score / a.total) * 100) : 0,
      })),
    [attempts],
  );

  const totalMin = perDay.reduce((s, d) => s + d.minuti, 0);

  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader
        tone="sage"
        icon={<TrendingUp className="h-5 w-5" />}
        kicker={t("stats.eyebrow")}
        title={t("stats.title")}
        subtitle={t("stats.subtitle")}
      />

      {sessionsQ.isPending ? <LoadingState /> : null}
      {sessionsQ.isError ? <ErrorState error={sessionsQ.error} onRetry={() => sessionsQ.refetch()} /> : null}

      <div className="grid grid-cols-2 gap-3">
        <SummaryCard icon={<Clock className="h-4 w-4" />} label={t("stats.minutesStudied")} value={totalMin} />
        <SummaryCard icon={<Award className="h-4 w-4" />} label={t("stats.quizzesDone")} value={attempts.length} />
      </div>

      <Card className="card-soft border-0 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <TrendingUp className="h-4 w-4 text-primary" /> {t("stats.minutesPerDay")}
        </div>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={perDay}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} width={30} />
              <Tooltip />
              <Bar dataKey="minuti" fill="var(--color-primary)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="card-soft border-0 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <PieIcon className="h-4 w-4 text-primary" /> {t("stats.subjects")}
        </div>
        {bySubject.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("stats.noSessionsYet")}</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={bySubject} dataKey="minuti" nameKey="name" outerRadius={70} label>
                  {bySubject.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="card-soft border-0 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <TrendingUp className="h-4 w-4 text-primary" /> {t("stats.quizAccuracy")}
        </div>
        {accuracy.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("stats.noQuizzesYet")}</p>
        ) : (
          <div className="h-48">
            <ResponsiveContainer>
              <LineChart data={accuracy}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="n" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={30} />
                <Tooltip />
                <Line type="monotone" dataKey="acc" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <FamilyReports />
    </div>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card className="card-soft border-0 p-4">
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}
